import { useCallback, useEffect, useState } from "react";
import { firebaseAuth, firestore } from "../firebase";
import {
  arrayUnion,
  doc,
  getDoc,
  WithFieldValue,
  writeBatch,
} from "firebase/firestore";
import { getQuery, useNavigate } from "../utils";
import { ResultType } from "../types";

class DataError extends Error {
  result: ResultType | undefined = undefined;

  constructor(result: ResultType) {
    super();
    this.result = result;
  }
}

const results = {
  [ResultType.bad_invitation]: {
    src: "/images/bad.png",
    title: "잘못된 초대장입니다.",
    desc: "선생님에게 다시 요청해주세요.",
  },
  [ResultType.bad_user]: {
    src: "/images/bad.png",
    title: "잘못된 초대장입니다.",
    desc: "선생님에게 다시 요청해주세요.",
  },
  [ResultType.invited]: {
    src: "/images/invited.png",
    title: "초대가 완료되었습니다.",
    desc: "태블릿 앱에서 클래스 탭을 확인해주세요.",
  },
  [ResultType.expired]: {
    src: "/images/expired.png",
    title: "만료된 초대장입니다.",
    desc: "선생님에게 문의해주세요.",
  },
  [ResultType.expired_included]: {
    src: "/images/expired_included.png",
    title: "이미 소속된 학원입니다.",
    desc: "태블릿 앱에서 클래스 탭을 확인해주세요.",
  },
  [ResultType.expired_included_school]: {
    src: "/images/expired_included.png",
    title: "이미 소속된 학교입니다.",
    desc: "태블릿 앱에서 클래스 탭을 확인해주세요.",
  },
};

export const Result = () => {
  const navigate = useNavigate();
  const [result, setResult] = useState<ResultType | undefined>(
    (getQuery("result") as ResultType) ?? undefined
  );

  const updateDocs = useCallback(async () => {
    try {
      const { search } = window.location;
      const organization = search.split("organization=")[1]?.split("&")?.[0];
      const invitation = search.split("invitation=")[1]?.split("&")?.[0];
      const userId = firebaseAuth.currentUser?.uid;
      if (!userId) {
        throw new DataError(ResultType.bad_user);
      }
      const invitationDocRef = doc(
        firestore,
        `organizations/${organization}/studentInvitations/${invitation}`
      );
      const userDocRef = doc(firestore, `users/${userId}`);
      const [invitationDoc, userDoc] = await Promise.all([
        getDoc(invitationDocRef),
        getDoc(userDocRef),
      ]);
      if (!invitationDoc) {
        throw new DataError(ResultType.bad_invitation);
      }
      if (!userDoc) {
        throw new DataError(ResultType.bad_user);
      }
      const invitationDocData = invitationDoc.data();
      if (invitationDocData?.status === "canceled") {
        throw new DataError(ResultType.expired);
      }
      if (invitationDocData?.status === "accepted") {
        const userDocData = userDoc.data();
        if (
          (userDocData?.organizationIds as Array<string>)?.includes(
            organization
          )
        ) {
          throw new DataError(ResultType.expired_included);
        }
        throw new DataError(ResultType.expired);
      }
      const batch = writeBatch(firestore);
      batch.set(
        invitationDocRef,
        {
          status: "accepted",
        },
        { merge: true }
      );
      batch.set(
        doc(firestore, `organizations/${organization}/students/${userId}`),
        {
          adminCode: "",
          isLeaved: false,
          isTutor: false,
          name: invitationDoc.data()?.name ?? "",
          parentPhoneNumber: null,
        }
      );
      const userDocUpdates: { [key: string]: WithFieldValue<unknown> } = {};
      const invitationData = invitationDoc.data() as { classroomIds: string[] };
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.organizationIds) {
          userDocUpdates.organizationIds = arrayUnion(organization);
        } else {
          userDocUpdates.organizationIds = [organization];
        }
        if (data.organizationClassroomIds) {
          userDocUpdates.organizationClassroomIds = arrayUnion(
            ...invitationData.classroomIds.map((classroomId) => ({
              classroomId,
              organizationId: organization,
            }))
          );
        } else {
          userDocUpdates.organizationClassroomIds =
            invitationData.classroomIds.map((classroomId) => ({
              classroomId,
              organizationId: organization,
            }));
        }
      } else {
        userDocUpdates.organizationIds = [organization];
        userDocUpdates.organizationClassroomIds =
          invitationData.classroomIds.map((classroomId) => ({
            classroomId,
            organizationId: organization,
          }));
      }
      batch.set(userDocRef, userDocUpdates, { merge: true });
      await batch.commit();
      setResult(ResultType.invited);
    } catch (e) {
      if (e instanceof DataError) {
        setResult(e.result);
      } else {
        alert("unknown error");
        navigate("/");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (result === undefined) {
      updateDocs();
    }
  }, [result, updateDocs]);

  if (result === undefined) return null;
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: 160,
      }}
    >
      <img
        alt="invitation card"
        src={results[result].src}
        style={{ width: 82, height: 88, marginBottom: 36 }}
      />
      <span style={{ fontWeight: "bold", fontSize: 20, marginBottom: 4 }}>
        {results[result].title}
      </span>
      <span style={{ fontWeight: "regular", fontSize: 16 }}>
        {results[result].desc}
      </span>
    </div>
  );
};
